<template>
    <asli-form @submitted="verifyWithRecaptcha">
        <asli-form-section title="Your Cover" heading-level="h2">
            <asli-dropdown
                id="previous_insurer"
                name="previous_insurer"
                rules="required:insurer"
                large
                v-model="previousPolicyDetails.insurer"
                label="Who is your previous insurer on this property?"
                :options="lists?.insurer"
                :placeholder="`Please select an option`"
            />
            <form-transition
                :become-visible-when="previousPolicyDetails.insurer !== null && showNoClaimsDiscountYears"
                @show="commitRisk"
            >
                <asli-radio
                    v-if="showNoClaimsDiscountYears"
                    id="no_claims_discount_years"
                    name="no_claims_discount_years"
                    v-model="previousPolicyDetails.no_claims_discount_years"
                    label="How many years insurance do you hold free of claims and/or losses?"
                    :options="ncdOptions"
                    rules="required:ncd_years"
                    :has-fixed-width-for-number-options="true"
                />
            </form-transition>
            <form-transition
                :become-visible-if="showHomeInsuranceClaimQuestion(previousPolicyDetails.no_claims_discount_years)"
                :become-visible-when="
                    previousPolicyDetails.insurer !== null &&
                    !showNoClaimsDiscountYears &&
                    previousPolicyDetails.insurer !== noPreviousInsurer
                "
                @show="commitRisk"
            >
                <asli-radio
                    v-if="previousPolicyDetails.insurer !== noPreviousInsurer"
                    id="has_claims"
                    name="has_claims"
                    v-model="previousPolicyDetails.has_claims"
                    label="Have you or any member of your household made a home insurance claim in the past 3 years?"
                    description="We will not be able to provide a quote if you have had a previous claim for flood, subsidence, heave or landslip."
                    :options="lists?.yes_no"
                    :is-yes-no-option="true"
                    rules="required:has_claims"
                    return-boolean
                />
            </form-transition>
            <form-transition :become-visible-when="previousPolicyDetails.has_claims === true" @show="commitRisk">
                <asli-repeater
                    v-if="
                        previousPolicyDetails.has_claims === true && previousPolicyDetails.insurer !== noPreviousInsurer
                    "
                    id="previous_policy_claims_repeater"
                    ref="claimsRepeaterRef"
                    v-model="previousPolicyDetails.claims"
                    :display-label="item => `${getListValueName('claim_type', item.type)}`"
                    v-slot="{ item, index, updateLocal }"
                    :add-label="`Add ${previousPolicyDetails.claims?.length ?? 0 > 0 ? 'Another' : 'a'} Claim`"
                    form-title="New Claim"
                    :object-template="EMPTY_CLAIM"
                    background-color="bg-panel-tertiary"
                    description="Please list each claim amount and date separately."
                    :always-show-cancel-btn="true"
                    :reset-repeater="resetRepeater"
                    @resetComplete="resetRepeater = false"
                    required-rule="required"
                >
                    <claim-form
                        :claim="item"
                        :ncdYears="previousPolicyDetails.no_claims_discount_years ?? 0"
                        @update="updateLocal(index, $event)"
                        :index="index"
                    />
                </asli-repeater>
            </form-transition>
            <form-transition
                :become-visible-when="
                    previousPolicyDetails.has_claims === false ||
                    (previousPolicyDetails.has_claims === true &&
                        previousPolicyDetails.claims &&
                        previousPolicyDetails.claims?.length > 0) ||
                    (!!previousPolicyDetails.no_claims_discount_years &&
                        !showHomeInsuranceClaimQuestion(previousPolicyDetails.no_claims_discount_years)) ||
                    previousPolicyDetails.insurer == noPreviousInsurer
                "
                :prevent-scroll="previousPolicyDetails.has_claims === true"
                @show="commitRisk"
            >
                <asli-text-input
                    v-if="showBuildingsCoverAmountQuestion"
                    id="buildings_cover_amount"
                    name="buildings_cover_amount"
                    autocomplete="transaction-amount"
                    large
                    :rules="`required:buildings_cover_amount|minRebuildAmount:140000|maxRebuildAmount:7000000`"
                    v-model="coverDetailsModel.cover_details.buildings_cover_amount"
                    label="What is the rebuilding cost of your home?"
                    description="Please note a minimum of €140,000 is required."
                    inputmode="numeric"
                    :icon="euroIcon()"
                    :mask="COMMA_NUMBER_MASK(7)"
                    :tooltipText="YOUR_COVER_HELP_TEXT.buildings_cover_amount"
                    :placeholder="`Min €140,000`"
                    :forced-error="buildingsAndContentsError ? BUILDINGS_CONTENTS_ERROR : null"
                    :hide-error="buildingsAndContentsError"
                />
            </form-transition>
            <form-transition
                :become-visible-when="
                    !!coverDetailsModel.cover_details.buildings_cover_amount ||
                    (!showBuildingsCoverAmountQuestion &&
                        (previousPolicyDetails.has_claims === false ||
                            (previousPolicyDetails.has_claims === true &&
                                previousPolicyDetails.claims &&
                                previousPolicyDetails.claims?.length > 0) ||
                            (!!previousPolicyDetails.no_claims_discount_years &&
                                !showHomeInsuranceClaimQuestion(previousPolicyDetails.no_claims_discount_years)) ||
                            previousPolicyDetails.insurer == noPreviousInsurer))
                "
                @show="commitRisk"
            >
                <asli-text-input
                    id="contents_cover_amount"
                    name="contents_cover_amount"
                    autocomplete="transaction-amount"
                    large
                    :rules="`required:contents_cover_amount|minAmountContents:15000|maxAmountContents:999999|sum_over_zero:buildings_cover_amount,${BUILDINGS_CONTENTS_ERROR}`"
                    v-model="coverDetailsModel.cover_details.contents_cover_amount"
                    label="How much would you like to insure your contents for?"
                    inputmode="numeric"
                    :icon="euroIcon()"
                    :mask="COMMA_NUMBER_MASK(7)"
                    :hide-error="buildingsAndContentsError"
                    :forced-error="buildingsAndContentsError ? BUILDINGS_CONTENTS_ERROR : null"
                    placeholder="Min €15,000"
                    :description="
                        showBuildingsCoverAmountQuestion &&
                        coverDetailsModel.cover_details.buildings_cover_amount?.toString() &&
                        'Contents can be entered as a percentage of the buildings sum insured or an amount chosen by you. If you enter an amount, average clause will apply. This means that if you undervalue the replacement cost of your buildings or contents, you will be responsible for the proportionate balance of the replacement cost in the event of a claim.'
                    "
                    :justify-between="true"
                    @change="resetContentsInsuranceButtons"
                >
                    <template #additional-content v-if="coverDetailsModel.cover_details.buildings_cover_amount">
                        <span class="text-center">or</span>
                        <div class="flex flex-col md:flex-row justify-between" role="group">
                            <asli-button
                                v-for="button in percentageButtons"
                                :key="button.id"
                                :id="button.id"
                                type="button"
                                class="mx-1 w-full my-1 md:my-0 !px-4"
                                :color="contentsInsuranceButtonsState[button.key] ? 'secondary' : 'grey'"
                                @click="calcContentsAmount(button.percentage, button.key)"
                            >
                                {{ button.label }}
                            </asli-button>
                        </div>
                    </template>
                </asli-text-input>
            </form-transition>
            <form-transition
                :become-visible-when="coverDetailsModel.cover_details.contents_cover_amount !== null"
                @show="commitRisk"
            >
                <asli-date
                    id="policy_start_date"
                    name="policy_start_date"
                    autocomplete="transaction-amount"
                    rules="required:start_date"
                    label="When do you require cover from i.e. policy/cover start date?"
                    v-model="coverDetailsModel.policy_details.start_date"
                    :date-from="{ date: startDateFrom, message: policyStartDateErrMessage }"
                    :date-to="{ date: startDateTo, message: policyStartDateErrMessage }"
                    calendar-aria-label="Open calendar to select policy start date"
                    :show-today="false"
                />
            </form-transition>
            <form-transition
                :become-visible-when="coverDetailsModel.policy_details.start_date !== null"
                @show="commitRisk"
            >
                <asli-radio
                    id="has_joint_proposers"
                    name="has_joint_proposers"
                    v-model="riskRef.has_joint_proposers"
                    label="Would you like to add a joint policyholder?"
                    :options="lists?.yes_no"
                    :is-yes-no-option="true"
                    rules="required"
                    return-boolean
                    :tooltip-text="YOUR_COVER_HELP_TEXT.has_joint_proposers"
                />
                <form-transition :become-visible-when="riskRef.has_joint_proposers === true" @show="commitRisk">
                    <asli-repeater
                        v-if="riskRef.has_joint_proposers === true"
                        id="joint_proposers_repeater"
                        ref="jointProposersRepeaterRef"
                        v-model="riskRef.joint_proposers"
                        v-slot="{ item, index, updateLocal }"
                        :display-label="item => `${item.personal_details.first_name} ${item.personal_details.surname}`"
                        :add-label="`Add ${riskRef.joint_proposers.length > 0 ? 'Another' : 'a'} Joint Proposer`"
                        form-title="Joint Proposer"
                        :object-template="EMPTY_JOINT_PROPOSER"
                        background-color="bg-panel-tertiary"
                        :always-show-cancel-btn="true"
                        required-rule="required"
                    >
                        <joint-proposer-form :index="index" :item="item" @update="updateLocal(index, $event)" />
                    </asli-repeater>
                </form-transition>
            </form-transition>
        </asli-form-section>
        <form-transition :become-visible-when="coverDetailsModel.policy_details.start_date !== null" @show="commitRisk">
            <asli-form-section title="Marketing Preferences" heading-level="h2">
                <asli-radio
                    id="is_marketing_allowed"
                    name="is_marketing_allowed"
                    rules="required:marketing_preferences"
                    v-model="allow_general_marketing"
                    label="From time to time we would like to contact you with details of products and services provided by us and the An Post group of companies by Email, SMS, Post, Phone. Is this OK? (You can withdraw your consent at any time.)"
                    :options="lists.yes_no"
                    :is-yes-no-option="true"
                    tooltip-text="You can withdraw your consent at any time by Phone, Post, or Email or through our website."
                    return-boolean
                />
                <form-transition :become-visible-when="allow_general_marketing === false" @show="commitRisk">
                    <asli-radio
                        v-if="!allow_general_marketing"
                        id="is_product_marketing_allowed"
                        name="is_product_marketing_allowed"
                        rules="required:marketing_preferences"
                        v-model="allow_product_marketing"
                        label="Would you like to only receive details and/or special offers in relation to our Home Insurance by Email, SMS, Post or Phone? Is this ok? (You can withdraw consent at any time.)"
                        :options="lists.yes_no"
                        :is-yes-no-option="true"
                        tooltip-text="You can withdraw your consent at any time by Phone, Post, or Email or through our website."
                        return-boolean
                    />
                </form-transition>
            </asli-form-section>
        </form-transition>
        <form-transition
            :become-visible-when="
                allow_general_marketing === true ||
                (allow_general_marketing === false && allow_product_marketing !== null)
            "
            @show="commitRisk"
        >
            <asli-form-section title="Terms and Conditions" heading-level="h2">
                <asli-checkbox
                    v-model="agreedToTermsAndConditions"
                    label=""
                    name="agreed-to-terms-and-conditions"
                    id="agreed-to-terms-and-conditions"
                    no-border
                    is-descriptive
                    is-custom-label
                    rules="required-terms-checkbox"
                >
                    <template #customLabel>
                        <div class="font-bold text-lg">
                            I confirm I have read and accept the
                            <asli-link :href="siteLinks.eligibilityCriteria" class="hover:underline !font-bold"
                                >Eligibility Criteria</asli-link
                            >,
                            <asli-link :href="siteLinks.privacyPolicy" class="hover:underline !font-bold"
                                >Privacy Policy</asli-link
                            >,
                            <asli-link :href="siteLinks.termsOfBusiness" class="hover:underline !font-bold"
                                >An Post Insurance Terms of Business</asli-link
                            >
                            &#38;
                            <asli-link :href="siteLinks.cookiesPolicy" class="hover:underline !font-bold"
                                >Cookies Policy</asli-link
                            >
                        </div>
                    </template>
                </asli-checkbox>
            </asli-form-section>
        </form-transition>
        <asli-panel
            v-if="coverDetailsModel.policy_details.start_date !== null"
            background="bg-panel-primary"
            class="mb-7 rounded-[5px] p-5 mt-[15px]"
        >
            <p class="mb-[10px] font-bold">
                {{ AnswerAccuratelyPanel.heading }}
            </p>
            <p class="text-sm">
                {{ AnswerAccuratelyPanel.description }}
            </p>
        </asli-panel>
        <div id="g-recaptcha"></div>
        <form-buttons
            @back="goBack"
            :show-continue="
                formIsEntirelyVisible &&
                (allow_general_marketing === true ||
                    (allow_general_marketing === false && allow_product_marketing !== null))
            "
            continueButtonTitle="Get an Indicative Price"
            prevent-scroll
        />
    </asli-form>
</template>

<script lang="ts" setup>
import {
    AsliButton,
    AsliLink,
    AsliCheckbox,
    AsliForm,
    AsliFormSection,
    AsliDate,
    AsliPanel,
    AsliRadio,
    AsliDropdown,
    AsliTextInput,
    AsliRepeater
} from "@mbs-npm/anpost-ui";
import { useRouter } from "vue-router";
import FormButtons from "@/components/FormButtons.vue";
import { ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { getResolvedRouteName } from "@/router/utils";
import { CoverDetailsSectionModel } from "@/stores/risk/types/types";
import { PreviousPolicyDetails } from "@/stores/risk/types/drivers/driver-types";
import { reactive, computed, watch, ref, onMounted, nextTick } from "vue";
import { getValidQuotes, useProposalStore } from "@/stores/risk/risk";
import { useListStore } from "@/stores/lists/lists";
import { euroIcon } from "@/static/constants";
import { siteLinks } from "@/utils/links";
import dayjs from "dayjs";
import { ncdOptions } from "@/static/constants";
import ClaimForm from "@/components/RepeaterForms/ClaimForm.vue";
import { EMPTY_CLAIM, EMPTY_JOINT_PROPOSER } from "@/stores/risk/types/repeater/empty-objects";
import { YOUR_COVER_HELP_TEXT } from "@/utils/helpText";
import { COMMA_NUMBER_MASK } from "@/static/masks";
import JointProposerForm from "@/components/RepeaterForms/JointProposerForm.vue";
import FormTransition from "@/components/FormTransitions/FormTransition.vue";
import { cloneDeep } from "lodash";
import { EMPTY_PREVIOUS_POLICY } from "@/stores/risk/static";
import { ResidencyOptions } from "@/stores/lists/types";
import { AnswerAccuratelyPanel } from "@/utils/labels";
import axios from "axios";
import { ApiEndpoints } from "@/static/api-endpoints";
import { AsliRepeaterInstance } from "@/stores/risk/types/repeater/repeater-types";
import { APP_STEP } from "@/stores/types";
import { goToError } from "@/utils/error-handling";

const mainStore = useMainStore();
const proposalStore = useProposalStore();
const listStore = useListStore();
const storedRisk = proposalStore.proposal.risk;
const lists = computed(() => listStore?.lists);
const { getListValueName } = listStore;
mainStore.setAppStep(APP_STEP.YOUR_COVER);

const router = useRouter();

const resetRepeater = ref(false);
const isNcdClearingClaims = ref(false);
const showNoClaimsDiscountYears = ref(false);
const previousNcdValue = ref<number | null | undefined>(null);
const agreedToTermsAndConditions = ref(null);
const recaptchaToken = ref<string | null>(null);
const claimsRepeaterRef = ref<AsliRepeaterInstance | null>(null);
const jointProposersRepeaterRef = ref<AsliRepeaterInstance | null>(null);

const isRecaptchaEnabled = window.VITE_RECAPTCHA_ENABLED === "true";

const policyStartDateErrMessage = "Policy must start within 30 days from today";

const noPreviousInsurer = "NoPreviousInsurer";

const residencyOwnershipStatus = ref<ResidencyOptions | null>(
    storedRisk.residency_details?.residence_ownership_status ?? null
);

const showBuildingsCoverAmountQuestion = computed(() => {
    if (isApartmentType(storedRisk?.property_details?.type)) return false;

    if (!isApartmentType(storedRisk?.property_details?.type)) {
        return (
            residencyOwnershipStatus.value === ResidencyOptions.IsLandlord ||
            residencyOwnershipStatus.value === ResidencyOptions.OwnerOccupied ||
            residencyOwnershipStatus.value === ResidencyOptions.IsHolidayHome ||
            residencyOwnershipStatus.value === ResidencyOptions.UnderRenovation ||
            residencyOwnershipStatus.value === ResidencyOptions.Unoccupied
        );
    }

    return !(residencyOwnershipStatus.value === ResidencyOptions.IsNotLandlord);
});

const riskRef = reactive({
    has_joint_proposers: storedRisk?.has_joint_proposers ?? null,
    joint_proposers: storedRisk?.joint_proposers ?? []
});

const allow_general_marketing = ref(proposalStore.proposal.allow_general_marketing);
const allow_product_marketing = ref(proposalStore.proposal.allow_product_marketing);

const coverDetailsModel = ref<CoverDetailsSectionModel>({
    policy_details: {
        ...storedRisk?.policy_details,
        start_date: storedRisk?.policy_details?.start_date,
        property_type: storedRisk?.property_details.type
    },
    cover_details: {
        ...storedRisk?.cover_details,
        buildings_cover_amount: storedRisk?.cover_details?.buildings_cover_amount,
        contents_cover_amount: storedRisk?.cover_details?.contents_cover_amount
    }
});

const previousPolicyDetails = reactive<Partial<PreviousPolicyDetails>>({
    insurer: storedRisk?.proposer?.previous_policy_details?.insurer,
    no_claims_discount_years: storedRisk?.proposer?.previous_policy_details?.no_claims_discount_years,
    has_claims: storedRisk?.proposer?.previous_policy_details?.has_claims,
    claims: storedRisk?.proposer?.previous_policy_details?.claims ?? []
});

const startDateFrom = new Date();
const startDateFromDay = dayjs(startDateFrom);
const startDateTo = computed(() => startDateFromDay.add(30, "day").toDate());

const buildingAndContentsTotal = computed(() => {
    const { buildings_cover_amount: buildingsCover, contents_cover_amount: contentsCover } =
        coverDetailsModel.value.cover_details;

    if (!buildingsCover || !contentsCover) {
        return 1;
    }

    return buildingsCover + contentsCover;
});

const buildingsAndContentsError = computed(() => {
    return buildingAndContentsTotal.value <= 0;
});

const BUILDINGS_CONTENTS_ERROR = "Please enter the contents amount you would like to insure with a minimum of €10,000";

interface ContentsInsuranceButtonsState {
    twenty: boolean;
    thirty: boolean;
    forty: boolean;
    fifty: boolean;
}

const contentsInsuranceButtonsInitialState: ContentsInsuranceButtonsState = {
    twenty: false,
    thirty: false,
    forty: false,
    fifty: false
};

const percentageButtons = [
    { id: "btn-contents-20", percentage: 0.2, label: "20%", key: "twenty" as const },
    { id: "btn-contents-30", percentage: 0.3, label: "30%", key: "thirty" as const },
    { id: "btn-contents-40", percentage: 0.4, label: "40%", key: "forty" as const },
    { id: "btn-contents-50", percentage: 0.5, label: "50%", key: "fifty" as const }
];

const contentsInsuranceButtonsState = reactive<ContentsInsuranceButtonsState>({
    ...contentsInsuranceButtonsInitialState
});

function updateContentsInsuranceButtonsState(newState: Partial<ContentsInsuranceButtonsState>) {
    Object.assign(contentsInsuranceButtonsState, contentsInsuranceButtonsInitialState, newState);
}

function resetContentsInsuranceButtons() {
    updateContentsInsuranceButtonsState({});
}

function calcContentsAmount(percentage: number, buttonKey: keyof ContentsInsuranceButtonsState) {
    const buildingsAmount = coverDetailsModel.value.cover_details.buildings_cover_amount;

    if (!buildingsAmount) return;

    const cleanAmount = parseInt(buildingsAmount.toString().replace(/,/g, ""));

    if (isNaN(cleanAmount)) return;

    coverDetailsModel.value.cover_details.contents_cover_amount = Math.trunc(cleanAmount * percentage);
    updateContentsInsuranceButtonsState({ [buttonKey]: true });
}

const formIsEntirelyVisible = computed(() => allow_general_marketing.value !== null);

function showHomeInsuranceClaimQuestion(val) {
    const valuesToShow = [0, 1, 2, 3];
    return valuesToShow.indexOf(val) !== -1;
}

function goBack() {
    commitRisk();
    router.push({
        name: getResolvedRouteName(ROUTE_NAMES.YourProperty, router.currentRoute.value),
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

const isApartmentType = buildingType => {
    return buildingType === "PurposeBuiltApartment" || buildingType === "Maisonette" || buildingType === "Flat";
};

function commitRisk() {
    coverDetailsModel.value.cover_details.buildings_cover_amount =
        !!coverDetailsModel.value.cover_details.buildings_cover_amount ||
        coverDetailsModel.value.cover_details.buildings_cover_amount === 0
            ? parseInt(coverDetailsModel.value.cover_details.buildings_cover_amount.toString().replace(/,/g, ""))
            : null;

    coverDetailsModel.value.cover_details.contents_cover_amount =
        !!coverDetailsModel.value.cover_details.contents_cover_amount ||
        coverDetailsModel.value.cover_details.contents_cover_amount === 0
            ? parseInt(coverDetailsModel.value.cover_details.contents_cover_amount.toString().replace(/,/g, ""))
            : null;

    // default the buildings cover amount if building type matches specific types.
    if (isApartmentType(storedRisk?.property_details?.type)) {
        coverDetailsModel.value.cover_details.buildings_cover_amount = null;
    }

    // Claims amount
    if (previousPolicyDetails.claims) {
        for (let i = 0; i < previousPolicyDetails.claims?.length; i++) {
            if (previousPolicyDetails.claims[i].amount) {
                previousPolicyDetails.claims[i].amount = parseInt(
                    previousPolicyDetails?.claims[i].amount!?.toString().replace(/,/g, "")
                );
            }
        }
    }

    proposalStore.patchPolicyDetails(coverDetailsModel.value.policy_details);
    proposalStore.patchCoverDetails(coverDetailsModel.value.cover_details);
    proposalStore.patchPreviousPolicyDetails(previousPolicyDetails);
    proposalStore.patchJointProposerDetails(riskRef);
    proposalStore.patchGeneralMarketing(allow_general_marketing.value);
    proposalStore.patchProductMarketing(allow_product_marketing.value);
}

async function goNext() {
    if (!riskRef.has_joint_proposers) {
        riskRef.joint_proposers = [];
    }

    if (!previousPolicyDetails.has_claims) {
        previousPolicyDetails.claims = [];
    }

    if (
        residencyOwnershipStatus.value === ResidencyOptions.IsLandlord &&
        coverDetailsModel.value.cover_details.has_sports_equipment
    ) {
        coverDetailsModel.value.cover_details.has_sports_equipment = false;
        coverDetailsModel.value.cover_details.sports_equipment_amount = null;
    }

    commitRisk();
    mainStore.setGuardStep(APP_STEP.QUOTES);
    const main = useMainStore();
    main.setLoadingState(true);
    main.setLoadingStatePopupText(`We are calculating our best online rate for you today... <br/><br/>
        Our promise is that you will not be able to better it by calling our Call Centre.`);

    if (allow_general_marketing.value) {
        proposalStore.patchProductMarketing(null);
    }

    try {
        if (proposalStore.proposal.uuid) {
            await proposalStore.updateProposal();
        } else {
            await proposalStore.createProposal();
        }
        await proposalStore.createQuote();
        main.setLoadingState(false);
        if (getValidQuotes(proposalStore.proposal.quotes).length > 0) {
            await router.push({
                name: ROUTE_NAMES.YourQuotes
            });
        } else {
            await router.push({
                name: ROUTE_NAMES.Declined
            });
        }
    } catch (e) {
        main.setLoadingState(false);
    }
}

async function verifyWithRecaptcha() {
    if (!agreedToTermsAndConditions.value) {
        return;
    }

    const claimsRepeater = claimsRepeaterRef.value;
    const jointRepeater = jointProposersRepeaterRef.value;

    if (claimsRepeater) {
        try {
            const isClaimsValid = await claimsRepeater?.validateCurrentForm(true);
            if (!isClaimsValid) return false;
        } catch (error) {
            console.log("error: ", error);
            return false;
        }
    }
    if (jointRepeater) {
        try {
            const isJointValid = await jointRepeater?.validateCurrentForm(true);
            if (!isJointValid) return false;
        } catch (error) {
            console.log("error: ", error);
            return false;
        }
    }

    // if both are valid - proceed
    if (isRecaptchaEnabled) {
        grecaptcha.execute();
    } else {
        await goNext();
    }
}

async function sendRecaptchaTokenToBackend() {
    recaptchaToken.value = grecaptcha.getResponse();
    const data = {
        response: recaptchaToken.value
    };
    try {
        const response = await axios.post(ApiEndpoints.RecaptchaVerify, data);
        response.data.success ? await goNext() : goToError(proposalStore.proposal.uuid as string);
    } catch (error) {
        console.log(error);
        throw error;
    }
}

watch(
    previousPolicyDetails,
    () => (showNoClaimsDiscountYears.value = previousPolicyDetails.insurer !== "NoPreviousInsurer"),
    { immediate: true }
);

watch(
    () => previousPolicyDetails.no_claims_discount_years,
    async (newValue, oldValue) => {
        previousNcdValue.value = oldValue;

        if ((newValue ?? 0) > 3) {
            isNcdClearingClaims.value = true;
            previousPolicyDetails.claims = [];
            previousPolicyDetails.has_claims = null;
            isNcdClearingClaims.value = false;
        } else {
            isNcdClearingClaims.value = true;
            previousPolicyDetails.claims = [];

            if ((previousNcdValue.value ?? 0) > 3) {
                previousPolicyDetails.has_claims = null;
            }

            resetRepeater.value = false;
            await nextTick();
            resetRepeater.value = true;
            isNcdClearingClaims.value = false;
        }
    }
);

watch(
    () => previousPolicyDetails.claims,
    newValue => {
        if (newValue?.length === 0 && !isNcdClearingClaims.value) {
            previousPolicyDetails.has_claims = false;
        }
    }
);

watch(
    () => riskRef.joint_proposers,
    newValue => {
        if (newValue?.length === 0) {
            riskRef.has_joint_proposers = false;
        }
    }
);

onMounted(() => {
    if (isRecaptchaEnabled) {
        grecaptcha.ready(function () {
            grecaptcha.render(
                "g-recaptcha",
                {
                    sitekey: window.RECAPTCHA_SITE_KEY,
                    size: "invisible",
                    callback: sendRecaptchaTokenToBackend
                },
                false
            );
        });
    }
    if (storedRisk?.proposer?.previous_policy_details === null) {
        storedRisk.proposer.previous_policy_details = cloneDeep(EMPTY_PREVIOUS_POLICY);
    }
    if (!showBuildingsCoverAmountQuestion.value) {
        coverDetailsModel.value.cover_details.buildings_cover_amount = 0;
    } else if (coverDetailsModel.value.cover_details.buildings_cover_amount === 0) {
        coverDetailsModel.value.cover_details.buildings_cover_amount = null;
    }
});
</script>
